import { SUPPORTED_EXCHANGES } from "../constants";
import { z } from "zod";

export type TokenPairSymbol = `${string}/${string}`;

// Token tickers across CEXes commonly include `.`, `-`, `_` as in-symbol
// markers (e.g. BitMart `EMRL.D`, bridged `USDC.E`, gate's `MEME-1B`,
// versioned `WETH_v2`). Keep `/` as the base/quote separator and the
// optional `:QUOTE` suffix for perpetual contracts. HIP-3 markets are stored
// with CLEAN symbols ("GOLD/USDC:USDC"); the `dex:` venue namespace is resolved
// backend-side and never reaches a stored/validated symbol, so no prefix here.
export const tokenPairSymbolSchema = z
  .string()
  .regex(
    /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+(:[A-Za-z0-9._-]+)?$/,
    "Pair symbol must be in the format 'BASE/QUOTE' or 'BASE/QUOTE:QUOTE' for perpetuals",
  )
  .min(1, "Pair symbol is required")
  .transform((val) => val as TokenPairSymbol);

/** Never watched: their full-depth books cost Kenobi more than every campaign pair combined. */
export const UNWATCHED_BASE_ASSETS: readonly string[] = ["BTC", "ETH"];

export const isWatchablePairSymbol = (pairSymbol: string): boolean => {
  const [base] = pairSymbol.split("/");
  return base === undefined || !UNWATCHED_BASE_ASSETS.includes(base.toUpperCase());
};

export const StartWatchingRequestSchema = z.object({
  exchangeId: z.nativeEnum(SUPPORTED_EXCHANGES),
  pairSymbol: tokenPairSymbolSchema.refine(isWatchablePairSymbol, {
    message: `${UNWATCHED_BASE_ASSETS.join(" and ")} pairs are not watched`,
  }),
  correlationId: z.string().min(1, "Correlation ID is required"),
  owner: z.string().optional(),
  endAt: z.coerce.date().optional(),
});

export const StopWatchingRequestSchema = z.object({
  exchangeId: z.nativeEnum(SUPPORTED_EXCHANGES),
  pairSymbol: tokenPairSymbolSchema,
  correlationId: z.string().min(1, "Correlation ID is required"),
});

export const StartWatchingResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});
export const StopWatchingResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

// Active averages response schemas
export const userFriendlyPeriodSchema = z.enum(["1h", "6h", "12h", "24h", "2d", "3d", "30d"]);

export const AveragePeriodDataSchema = z.object({
  period: userFriendlyPeriodSchema,
  // averageSpread is the primary ($100) notional. averageSpread30/50 are the
  // effective spreads at the smaller USD notionals. Each is null when there is
  // no data (or insufficient liquidity) for that notional in the period.
  averageSpread: z.string().nullable(),
  averageSpread30: z.string().nullable(),
  averageSpread50: z.string().nullable(),
  // One count per notional, matching the spread above it: a notional that runs out
  // of book contributes no sample, so a shared count would describe a spread other
  // than the one it is read with.
  sampleCount: z.number().int().nonnegative(),
  sampleCount30: z.number().int().nonnegative(),
  sampleCount50: z.number().int().nonnegative(),
  fromTimestamp: z.coerce.date(),
  toTimestamp: z.coerce.date(),
  inProgress: z.boolean().optional(), // True when period is not fully covered (watcher started after period began)
});

export const ActiveAverageItemSchema = z.object({
  exchangeId: z.nativeEnum(SUPPORTED_EXCHANGES),
  pairSymbol: tokenPairSymbolSchema,
  averages: z.array(AveragePeriodDataSchema),
  owner: z.string().optional(),
  startedAt: z.coerce.date().optional(),
});

// Lightweight watcher item for list-only endpoint (no averages computed)
export const WatcherItemSchema = z.object({
  exchangeId: z.nativeEnum(SUPPORTED_EXCHANGES),
  pairSymbol: tokenPairSymbolSchema,
  owner: z.string().optional(),
  startedAt: z.coerce.date(),
  expireAt: z.coerce.date(),
});

export const GetActiveWatchersResponseSchema = z.array(WatcherItemSchema);

export const GetActiveAveragesResponseSchema = z.array(ActiveAverageItemSchema);

export const GetPastAverageRecordingsResponseSchema = z.array(ActiveAverageItemSchema);

// Inferred types for TypeScript
export type StartWatchingRequest = z.infer<typeof StartWatchingRequestSchema>;
export type StopWatchingRequest = z.infer<typeof StopWatchingRequestSchema>;
export type StartWatchingResponse = z.infer<typeof StartWatchingResponseSchema>;
export type StopWatchingResponse = z.infer<typeof StopWatchingResponseSchema>;
export type UserFriendlyPeriod = z.infer<typeof userFriendlyPeriodSchema>;
export type AveragePeriodData = z.infer<typeof AveragePeriodDataSchema>;
export type ActiveAverageItem = z.infer<typeof ActiveAverageItemSchema>;
export type WatcherItem = z.infer<typeof WatcherItemSchema>;
export type GetActiveAveragesResponse = z.infer<typeof GetActiveAveragesResponseSchema>;
export type GetActiveWatchersResponse = z.infer<typeof GetActiveWatchersResponseSchema>;
export type GetPastAverageRecordingsResponse = z.infer<typeof GetPastAverageRecordingsResponseSchema>;

// Watcher health schemas
export const WatcherHealthStatusSchema = z.enum(["healthy", "degraded", "error"]);

export const WatcherHealthErrorSchema = z.object({
  message: z.string(),
  timestamp: z.number(),
  count: z.number().int().positive(),
});

export const WatcherHealthItemSchema = z.object({
  exchangeId: z.nativeEnum(SUPPORTED_EXCHANGES),
  pairSymbol: tokenPairSymbolSchema,
  lastSnapshotAt: z.number().nullable(),
  snapshotCount: z.number().int().nonnegative(),
  lastError: WatcherHealthErrorSchema.nullable(),
  status: WatcherHealthStatusSchema,
  updatedAt: z.number(),
});

export const GetWatcherHealthResponseSchema = z.array(WatcherHealthItemSchema);

export type WatcherHealthStatus = z.infer<typeof WatcherHealthStatusSchema>;
export type WatcherHealthError = z.infer<typeof WatcherHealthErrorSchema>;
export type WatcherHealthItem = z.infer<typeof WatcherHealthItemSchema>;
export type GetWatcherHealthResponse = z.infer<typeof GetWatcherHealthResponseSchema>;
